import React, { FC, useMemo } from 'react'
import { Box } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as ExpandIcon } from 'assets/svg/ExpandIcon.svg'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, YAxis } from 'recharts'
import Paper from '@material-ui/core/Paper/Paper'
import useTheme from '@material-ui/core/styles/useTheme'
import IconButton from '@material-ui/core/IconButton'
import { IChartProps } from 'typescript/interfaces/dashboard'
import { convertDate, formatPercentage } from 'utils/formatters'
import { DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import { bankRounding } from 'utils/rounding'

const TermFactorChart: FC<IChartProps> = ({ additionalRem = 0, onCollapse, stats, header }) => {
  const theme = useTheme()
  const fontSizeHtml = parseFloat(getComputedStyle(document.documentElement).fontSize)

  const data = stats?.data?.map(({ date, factor }) => ({ name: convertDate(new Date(date)), factor }))

  const maxLength = useMemo(() => {
    return data
      ? data
          .reduce((acc, val) => {
            return acc < val.factor ? val.factor : acc
          }, 0)
          .toString().length * fontSizeHtml
      : 0
  }, [data])

  return (
    <Paper elevation={0}>
      <Box height="100%" py="1rem" px="1rem" display="flex" flexDirection="column" justifyContent="space-between">
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body1">Factor AVG</Typography>
            <IconButton onClick={onCollapse} size="small">
              <SvgIcon fontSize="small" viewBox="0 0 16 16" component={ExpandIcon} />
            </IconButton>
          </Box>
          <Box>
            <Typography style={{ fontWeight: 600 }} variant="h4" color="textSecondary">
              {bankRounding(header?.factorAvg || 0, DECIMAL_PLACES_PERCENTAGE)}
            </Typography>
          </Box>
        </Box>
        <Box mt={3}>
          <ResponsiveContainer width="100%" height={fontSizeHtml * (6.45 + additionalRem)}>
            <AreaChart
              data={data}
              margin={{
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <YAxis
                allowDecimals={false}
                tick={{
                  fill: theme.palette.text.primary,
                  strokeWidth: 1,
                }}
                minTickGap={0}
                tickCount={4}
                width={maxLength}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                tickMargin={0}
                padding={{
                  top: 0,
                  bottom: 0,
                }}
              />
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <Tooltip
                labelFormatter={(index) => data?.[index as number]?.name}
                formatter={(value) => {
                  if (Array.isArray(value)) {
                    return value.map((v) => formatPercentage(v)).join(', ')
                  }
                  return formatPercentage(value)
                }}
              />
              <Area type="monotone" dataKey="factor" stroke={theme.palette.primary.main} fillOpacity={0.3} fill={theme.palette.primary.main} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  )
}

export default TermFactorChart
